/**
 * Sentry helpers for chat-web-sdk (project `chat-web-sdk` in Sentry).
 *
 * Noise reduction + defensive SDK usage for Linear EUX bundle: **1702** (PubNub auth 400), **1711–1712–1716** /
 * **1730** (listChannels health 403), **1714** (partial `getSentry` / missing `getClient`), **1715** (missing
 * `captureException`). See `implementation-plans/eux-chat-web-sdk-sentry-master-plan.md`.
 */
/** Dynamic import entry point; tests spy `load` so failures and stubs apply to all `getSentry()` callers. */
export const sentryBrowserImport = {
  load(): Promise<typeof import('@sentry/browser')> {
    return import(/* webpackChunkName: "sentry" */ '@sentry/browser');
  },
};

export function getSentry() {
  return sentryBrowserImport.load();
}

/** `fetch()` network-layer rejections use this constructor name (e.g. Safari "Load failed"). */
const TYPE_ERROR_NAME = 'TypeError';

/** Message fragment after "TypeError: " — same shape as `Failed to fetch` / `Load failed`. */
const SAFE_TO_IGNORE_TYPE_ERROR_MESSAGES = [
  'Failed to fetch',
  'Load failed', // WebKit / Safari when fetch() never completes
  'NetworkError when attempting to fetch resource.',
  'cancelled',
  'Abgebrochen', // German
  'Kumottu', // Finnish
  'annullato', // Italian
  'annulleret', // Danish
  'avbrutt', // Norwegian
  'geannuleerd', // Dutch
  'poništeno', // Bosnian
  'vazgeçildi', // Turkish
  'visszavonva', // Hungarian
  'скасовано', // Ukrainian
  'ถูกยกเลิก', // Thai
  'キャンセルしました', // Japanese
  '已取消', // Chinese
  '취소됨', // Korean
];

/**
 * Turns message fragments into Sentry `ignoreErrors` strings (`TypeError: <message>`).
 * Exported for unit tests.
 */
export function buildSentryTypeErrorIgnorePatterns(
  messages: readonly string[],
): string[] {
  return messages.map((msg) => `${TYPE_ERROR_NAME}: ${msg}`);
}

/** Stack path fragments that indicate PubNub health-check / recovery (narrow 403 filter). */
const PUBNUB_HEALTH_CHECK_FRAME_HINTS = [
  'pubnub-health-check',
  'pubnub-message-recovery',
  'checkandreconnect',
  'markundeliveredmessages',
] as const;

interface SentryStackFrame {
  filename?: string;
  abs_path?: string;
  module?: string;
}

interface SentryExceptionValue {
  value?: string;
  mechanism?: { type?: string };
  stacktrace?: {
    frames?: SentryStackFrame[];
  };
}

interface SentryBeforeSendEvent {
  exception?: {
    values?: SentryExceptionValue[];
  };
  contexts?: {
    response?: {
      status_code?: number;
    };
  };
  request?: {
    url?: string;
  };
}

function collectSentryFramePaths(event: SentryBeforeSendEvent): string[] {
  return (event.exception?.values ?? []).flatMap((ex) =>
    (ex.stacktrace?.frames ?? []).flatMap((frame) => {
      const parts: string[] = [];
      if (frame.filename) parts.push(frame.filename.toLowerCase());
      if (frame.abs_path) parts.push(frame.abs_path.toLowerCase());
      if (frame.module) parts.push(frame.module.toLowerCase());
      return parts;
    }),
  );
}

function isHttpClient403(event: SentryBeforeSendEvent): boolean {
  const statusCode = event.contexts?.response?.status_code;
  if (statusCode === 403) return true;
  return (
    event.exception?.values?.some(
      (v) =>
        typeof v.value === 'string' && v.value.includes('status code: 403'),
    ) ?? false
  );
}

function isHttpClient400(event: SentryBeforeSendEvent): boolean {
  const statusCode = event.contexts?.response?.status_code;
  if (statusCode === 400) return true;
  return (
    event.exception?.values?.some(
      (v) =>
        typeof v.value === 'string' && v.value.includes('status code: 400'),
    ) ?? false
  );
}

/** Message shape for synthetic `http.client` errors (Sentry HTTP client integration). */
const HTTP_CLIENT_STATUS_IN_MESSAGE = /status code:\s*(\d{3})/i;

/** Status from `contexts.response` or synthetic `http.client` exception text. */
function parseHttpClientStatusCode(
  event: SentryBeforeSendEvent,
): number | undefined {
  const fromContext = event.contexts?.response?.status_code;
  if (typeof fromContext === 'number') return fromContext;
  let match: RegExpExecArray | null = null;
  (event.exception?.values ?? []).find((v) => {
    if (typeof v.value !== 'string') return false;
    const m = HTTP_CLIENT_STATUS_IN_MESSAGE.exec(v.value);
    if (m) {
      match = m;
      return true;
    }
    return false;
  });
  return match ? parseInt(match[1], 10) : undefined;
}

function hasPubNubHealthCheckFrame(paths: string[]): boolean {
  return paths.some((p) =>
    PUBNUB_HEALTH_CHECK_FRAME_HINTS.some((hint) => p.includes(hint)),
  );
}

/**
 * Drop known-noise `http.client` events (from `httpClientIntegration` on failed 4xx fetch/XHR; 5xx are not captured — see `shouldDropHttpClient5xx`).
 *
 * **403 + health/recovery frames:** PubNub `listChannels` / channel-registration returns 403 when PAM/token
 * does not allow the probe; `verifySubscriptionsActive` still times out to “unhealthy” without needing Sentry
 * spam (EUX-1711, EUX-1712, EUX-1716, EUX-1730 / WNA — same pattern). We require stack/chunk hints
 * (`checkAndReconnect`, `pubnub-health-check`, etc.) so we do **not** drop arbitrary PubNub 403s (e.g. raw
 * `instance.ts` only). See `pubnub-403-sentry-health-check-investigation.md`.
 *
 * **400 + `/pubnub/auth`:** Expected for some orgs; runtime already degrades in `initializePubNub` (EUX-1702,
 * EUX-1698).
 */
export function shouldDropPubNubHttpClientNoise(
  event: SentryBeforeSendEvent,
): boolean {
  const mechanism = event.exception?.values?.[0]?.mechanism?.type;
  if (mechanism !== 'http.client') return false;

  if (
    isHttpClient403(event) &&
    hasPubNubHealthCheckFrame(collectSentryFramePaths(event))
  ) {
    return true;
  }

  if (isHttpClient400(event)) {
    const url = (event.request?.url ?? '').toLowerCase();
    if (url.includes('/pubnub/auth')) return true;
  }

  return false;
}

/**
 * Drop `http.client` events for **5xx** responses. Backend alerting owns server errors; the SDK should not spam Sentry with them.
 *
 * Used in `beforeSend` as defense in depth alongside `httpClientIntegration({ failedRequestStatusCodes: [[400, 499]] })`.
 */
export function shouldDropHttpClient5xx(event: SentryBeforeSendEvent): boolean {
  if (event.exception?.values?.[0]?.mechanism?.type !== 'http.client') {
    return false;
  }
  const code = parseHttpClientStatusCode(event);
  return code !== undefined && code >= 500 && code <= 599;
}

export function initSentry(key, orgId?) {
  return getSentry()
    .catch((chunkError) => {
      // Silently fail if Sentry chunk fails to load (network issues, CDN problems, etc.)
      // This prevents chunk loading failures from breaking initialization
      console.warn(
        'Failed to load Sentry chunk for initialization:',
        chunkError,
      );
      return undefined;
    })
    .then((Sentry) => {
      if (!Sentry) return undefined;
      // todo set org and user info (tracking token? customer id?)
      // EUX-1714 / CHAT-WEB-SDK-WN4: dynamic import can resolve to a partial namespace on some WebKit loads
      // where `getClient` is missing. Calling it blindly throws and prevents init; missing `init` would also throw.
      const hasExistingClient =
        typeof Sentry.getClient === 'function' && Sentry.getClient();
      if (!hasExistingClient && typeof Sentry.init === 'function') {
        Sentry.init({
          dsn: `https://${key}@o38471.ingest.sentry.io/6150287`, // 6150287 is project ID. not sure what o38471 is
          environment: process.env.SENTRY_ENV,
          release: process.env.VERSION,
          initialScope: {
            tags: { orgId },
          },
          beforeSend(event) {
            // PubNub/Kustomer chat HTTP noise (see `shouldDropPubNubHttpClientNoise` JSDoc).
            if (shouldDropPubNubHttpClientNoise(event)) {
              return null;
            }
            if (shouldDropHttpClient5xx(event)) {
              return null;
            }
            if (event.exception?.values?.length) {
              // Even though values is an array, we can safely assume that the first one is our error.
              // The only time there will be more than one exeption is with linked errors, which we are not using.
              const exception = event.exception.values[0];
              // Allow HTTP client integration events to pass through (they don't have stacktraces)
              // These events have mechanism.type === 'http.client' and are synthetic errors for captured 4xx responses
              const isHttpClientError =
                exception.mechanism?.type === 'http.client';
              // Only filter out JavaScript errors that lack stacktraces
              // HTTP client errors should be reported even without stacktraces
              if (!isHttpClientError && !exception.stacktrace) {
                return null;
              }
            }
            return event;
          },
          integrations: [
            Sentry.httpClientIntegration({
              failedRequestStatusCodes: [[400, 499]],
            }),
          ],
          ignoreErrors: buildSentryTypeErrorIgnorePatterns(
            SAFE_TO_IGNORE_TYPE_ERROR_MESSAGES,
          ),
        });
      }
      return Sentry;
    });
}

export function captureException(error, context) {
  return getSentry()
    .catch((chunkError) => {
      // Silently fail if Sentry chunk fails to load (network issues, CDN problems, etc.)
      // The error has already been logged to console via handleError, so we don't need to log again
      // This prevents chunk loading failures from causing cascading errors
      console.warn(
        'Failed to load Sentry chunk for error reporting:',
        chunkError,
      );
      return undefined;
    })
    .then((Sentry) => {
      // EUX-1715 / CHAT-WEB-SDK-WN5: same partial-import issue as EUX-1714 — avoid cascading TypeError in the reporter.
      if (!Sentry || typeof Sentry.captureException !== 'function') {
        return undefined;
      }
      return Sentry.captureException(error, context);
    });
}
